import React, { useEffect, useState } from 'react'
import Banner2 from '../../components/Banner2'
import { Col, Container, Form, Row } from 'react-bootstrap'
import { useDispatch } from 'react-redux'
import { createProductInquiry } from '../../features/productInquiry'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import SelectDropdown from '../../components/SelectDropdown'
import { getCountries, getCountryCallingCode } from "libphonenumber-js";
import MobileNumberInput from '../../components/MobileNumberInput'

const HomeInsurance = () => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const navigate = useNavigate()
  const { t } = useTranslation()

  const handleInputChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.title) newErrors.title = t("validation.title_required");
    if (!formData.name?.trim()) newErrors.name = t("validation.name_required");
    if (!formData.building_value) newErrors.building_value = t("validation.required");
    if (!formData.total_approx || formData.total_approx <= 0)
      newErrors.total_approx = t("validation.invalid_amount");
    if (!formData.address?.trim()) newErrors.address = t("validation.required");

    if (!formData.email) {
      newErrors.email = t("validation.email_required");
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = t("validation.email_invalid");
    }

    if (!formData.mobile_number) {
      newErrors.mobile_number = t("validation.phone_required");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error(t("validation.please_fix_errors"));
      return;
    }

    try {
      const response = await dispatch(createProductInquiry({
        form_data: formData,
        product_details: JSON.parse(localStorage.getItem('product_details')),
      })).unwrap();

      if (response.success) {
        toast.success(t('success_res'));
        navigate('/');
      }
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error(t('error_res'));
    }
  };

  return (
    <>
      <Banner2 classList='bg-grey'>
        <Container>
          <Row>
            <Col className='offset-md-3' md={9}>
              <div className='insurance-title'>
                <h3 className='title text-capitalize text-center'>
                  {t('home_insurance.description')}
                </h3>
              </div>
            </Col>

            <Col md={3}>
              <div className='policy-box'>
                <div className='icon-image'>
                  <img
                    src={require('../../assets/SVG/1-28.svg').default}
                    alt="icon"
                    className="img-fluid"
                  />
                </div>
                <p className='title'>{t('home_insurance.title')}</p>
              </div>
            </Col>

            <Col md={9}>
              <div className='policy-form'>
                <div className='policy-formBox why-us-form'>
                  <Form onSubmit={handleSubmit}>
                    <Row>

                      <Col md={6} lg={3}>
                        <div className='form-group'>
                          <SelectDropdown
                            name="title"
                            placeholder={t('home_insurance.form.title.placeholder')}
                            selectedOption={formData.title ?? ""}
                            options={[
                              { value: "mr", label: t('home_insurance.form.title.option1') },
                              { value: "miss", label: t('home_insurance.form.title.option2') },
                              { value: "mrs", label: t('home_insurance.form.title.option3') },
                            ]}
                            handleOnChange={handleInputChange}
                          />
                          {errors.title && (
                            <small className="text-danger">{errors.title}</small>
                          )}
                        </div>
                      </Col>

                      <Col md={6} lg={9}>
                        <div className='form-group'>
                          <Form.Control
                            type='text'
                            name='name'
                            placeholder={t('home_insurance.form.name')}
                            className="form-control element-style"
                            onChange={(e) => handleInputChange(e.target.name, e.target.value)}
                          />
                          {errors.name && (
                            <small className="text-danger">{errors.name}</small>
                          )}
                        </div>
                      </Col>

                      <Col lg={6}>
                        <div className='form-group'>
                          <Form.Control
                            type='text'
                            name='building_value'
                            placeholder={t('home_insurance.form.building_value')}
                            className="form-control element-style"
                            onChange={(e) => handleInputChange(e.target.name, e.target.value)}
                          />
                          {errors.building_value && (
                            <small className="text-danger">{errors.building_value}</small>
                          )}
                        </div>
                      </Col>

                      <Col lg={6}>
                        <div className='form-group'>
                          <Form.Control
                            type='number'
                            name='total_approx'
                            placeholder={t('home_insurance.form.total_approx')}
                            className="form-control element-style"
                            onChange={(e) => handleInputChange(e.target.name, e.target.value)}
                          />
                          {errors.total_approx && (
                            <small className="text-danger">{errors.total_approx}</small>
                          )}
                        </div>
                      </Col>

                      <Col md={12}>
                        <div className='form-group'>
                          <Form.Control
                            type='text'
                            name='address'
                            placeholder={t('home_insurance.form.address')}
                            className="form-control element-style"
                            onChange={(e) => handleInputChange(e.target.name, e.target.value)}
                          />
                          {errors.address && (
                            <small className="text-danger">{errors.address}</small>
                          )}
                        </div>
                      </Col>

                      <Col md={6}>
                        <div className='form-group'>
                          <Form.Control
                            type='email'
                            name='email'
                            placeholder={t('home_insurance.form.email')}
                            className="form-control element-style"
                            onChange={(e) => handleInputChange(e.target.name, e.target.value)}
                          />
                          {errors.email && (
                            <small className="text-danger">{errors.email}</small>
                          )}
                        </div>
                      </Col>

                      <Col md={6}>
                        <MobileNumberInput
                          onChange={({ countryCode, number }) => {
                            handleInputChange("country_code", countryCode);
                            handleInputChange("mobile_number", number);
                          }}
                        />
                        {errors.mobile_number && (
                          <small className="text-danger">{errors.mobile_number}</small>
                        )}
                      </Col>

                      <Col md={12}>
                        <div className='form-submitbtn'>
                          <button type="submit" className="btn h-btn">
                            {t('home_insurance.form.confirm')}
                          </button>
                        </div>
                      </Col>

                    </Row>
                  </Form>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </Banner2>
    </>
  )
}

export default HomeInsurance